import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import DataTable from '../../components/Datatable';
import Loading from '../../components/Loading';
import { getSortKeys, setAdditionalStyles } from '../../helpers/table';
import { COLUMNS } from './keys';
import { getPeriods, summarizeDomainMetrics } from './metrics';
import { fetchDomainDailyMetrics, getUserDomains } from './query';

/**
 * How many properties are queried at once. Every row is fetched with its own request so that one
 * slow or failing property never holds up the others, but firing all of them simultaneously
 * would put every Search Console call for the account into the same instant and invite rate
 * limiting, so they are drained through a small pool instead.
 */
const CONCURRENT_REQUESTS = 6;

class DomainPerformance extends Component {
    constructor(props) {
        super();

        this.searchFunction = this.searchFunction.bind(this);

        this.credentialsDomainId = props.credentials_domain_id || '';
        this.periods = getPeriods();
        this.isMounted_ = false;

        this.state = {
            loadingDomains: true,
            // Set when the domain list itself could not be loaded, which is a different empty
            // table from "this account has no Search Console properties".
            domainsError: null,
            rows: [],
        };
    }

    async componentDidMount() {
        this.isMounted_ = true;

        let domains = [];
        let domainsError = null;
        try {
            domains = await getUserDomains();
        } catch (err) {
            console.log(err);
            domainsError = err?.message ?? this.props.t('global.something_went_wrong');
        }

        if (!this.isMounted_) return;

        // The table is rendered from the domain list alone; each row then shows a loader in its
        // metric cells until its own request comes back.
        const rows = domains.map((domain) => ({
            id: domain.siteUrl,
            siteUrl: domain.siteUrl,
            name: domain.name,
            permissionLevel: domain.permissionLevel,
            status: this.credentialsDomainId ? 'loading' : 'error',
            error: this.credentialsDomainId
                ? null
                : this.props.t('tools.domain_performance.no_domain'),
            metrics: null,
        }));

        this.setState({ rows, domainsError, loadingDomains: false });

        if (!this.credentialsDomainId) return;

        this.loadMetrics(rows);
    }

    componentWillUnmount() {
        this.isMounted_ = false;
    }

    /**
     * Applies a patch to a single row. The updates land while up to CONCURRENT_REQUESTS fetches
     * are in flight, so this has to go through the functional form of setState — reading
     * this.state.rows directly would let two rows resolving in the same tick overwrite each
     * other.
     */
    updateRow(siteUrl, patch) {
        if (!this.isMounted_) return;
        this.setState((state) => ({
            rows: state.rows.map((row) => (row.siteUrl === siteUrl ? { ...row, ...patch } : row)),
        }));
    }

    async loadRowMetrics(row) {
        try {
            const daily = await fetchDomainDailyMetrics({
                credentialsDomainId: this.credentialsDomainId,
                siteUrl: row.siteUrl,
                startDate: this.periods.startDate,
                endDate: this.periods.endDate,
            });
            this.updateRow(row.siteUrl, {
                status: 'done',
                metrics: summarizeDomainMetrics(daily, this.periods),
            });
        } catch (err) {
            console.log(row.siteUrl, err);
            this.updateRow(row.siteUrl, {
                status: 'error',
                error: err?.message ?? this.props.t('global.something_went_wrong'),
            });
        }
    }

    async loadMetrics(rows) {
        const queue = [...rows];
        const workers = Array.from(
            { length: Math.min(CONCURRENT_REQUESTS, queue.length) },
            async () => {
                while (queue.length && this.isMounted_) {
                    await this.loadRowMetrics(queue.shift());
                }
            },
        );

        await Promise.all(workers);
    }

    searchFunction(data, search) {
        if (!search) return data;
        return data.filter((item) => item.siteUrl?.toLowerCase().includes(search.toLowerCase()));
    }

    render() {
        const { t } = this.props;
        const columns = COLUMNS(t);

        if (this.state.loadingDomains) return <Loading padding={20} />;
        console.log('this.state.rows', this.state.rows);

        return (
            <DataTable
                nodes={this.state.rows}
                header={t('tools.domain_performance.period')}
                COLUMNS={columns}
                sortFns={getSortKeys(columns)}
                searchFunction={this.searchFunction}
                fileName={t('tools.domain_performance.header')}
                customEmptyMessage={this.state.domainsError ?? t('global.no_data')}
                additionalStyles={setAdditionalStyles(columns)}
                customContainerStyles="domain-performance-table"
            />
        );
    }
}

export default withTranslation()(DomainPerformance);
